import { gql } from '@apollo/client';
import { Alert, Card, Checkbox, Col, Form, Row, Select } from 'antd';
import SettingLabel from 'components/Settings/SettingLabel';
import { useQuery } from 'hooks/apollo';
import React, { useEffect, useMemo } from 'react';
import {
  PLANS,
} from 'shared/constants/urls';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { SEK } from 'shared/constants/currencies';
import { Module, LicenseInterval, DataSourceType } from 'shared/prisma/types';
import { dayjs } from 'shared/utils/date';

import globalMessages from 'translations/messages';
import { getModuleLimitSetting, getValidLicenseInterval } from 'shared/utils/license';
import LicenseForm from './LicenseForm';

const modules = {
  [DataSourceType.Website]: [Module.Analytics, Module.Heatmaps, Module.UxSurveys],
  [DataSourceType.Cx]: [Module.CxSurveys],
};

const LICENSES = gql`
  query ActiveLicenses {
    activeModuleLicenses {
      id
      title
      module
      status
      level
      settings(keys: ["prices", "monthlySinglePrice", "monthlyMultiplePrice"])
    }
  }
`;

const ModuleLicenseCard = ({ module, loading, licenses, form, addons, onChange }) => {
  const interval = Form.useWatch(['interval'], form);
  const licenseId = Form.useWatch(['modules', module, 'licenseId'], form);
  const licenseOptions = licenses?.map(l => ({ value: l.id, label: l.title }));
  const license = licenses?.find(l => l.id === licenseId);
  const setting = getModuleLimitSetting(module, license?.level);

  const limitOptions = useMemo(() => license?.settings.prices?.filter(p => p[setting] != null).map(p => ({ value: p[setting], label: <FormattedNumber value={p[setting]} /> })), [license, setting]);

  useEffect(() => {
    form.setFieldValue(['modules', module, 'limit'], limitOptions?.[0]?.value);
    onChange();
  }, [limitOptions]);

  useEffect(() => {
    form.setFieldValue(['modules', module, 'addons'], []);
    onChange();
  }, [interval]);

  return (
    <Card title={<FormattedMessage {...globalMessages[module]} />} loading={loading}>
      <Form.Item name={['modules', module, 'licenseId']} label="License" rules={[{ required: true, message: 'Required' }]}>
        <Select options={licenseOptions} />
      </Form.Item>
      <Form.Item name={['modules', module, 'limit']} label={<SettingLabel setting={setting} />} rules={[{ required: true, message: 'Required' }]}>
        <Select options={limitOptions} />
      </Form.Item>
      <Form.Item name={['modules', module, 'addons']} label="Addons">
        <Checkbox.Group options={addons?.map(l => ({ value: l.id, label: l.title }))} disabled={interval === LicenseInterval.Monthly} />
      </Form.Item>
    </Card>
  );
};

const ModuleLicenseForm = ({ form, dataSource, onSubmit, onChange }) => {
  const { data, loading } = useQuery(LICENSES);

  const addons = data?.activeModuleLicenses.filter(l => l.module === Module.Addon);
  const availableModules = modules[dataSource.type];

  const getValues = (values) => ({
    ...values,
    modules: availableModules.map(module => values.modules[module]?.licenseId).filter(Boolean),
    settings: availableModules.flatMap(module => {
      const { licenseId } = values.modules[module];
      const license = data?.activeModuleLicenses.find(l => l.id === licenseId);
      const limitSetting = getModuleLimitSetting(module, license?.level);
      const priceSettings = license?.settings.prices.find(p => p[limitSetting] === values.modules[module].limit);
      const settings = priceSettings ? Object.keys(priceSettings).filter(key => priceSettings[key] != null && key !== 'monthlyPrice').map(key => ({
        key,
        value: priceSettings[key],
      })) : [];

      if (values.modules[module].addons?.length > 0) {
        settings.push({
          key: `${module}-addons`,
          value: values.modules[module].addons,
        });
      }

      return settings;
    }),
    licenses: data?.activeModuleLicenses,
  });

  const onFinish = async (values) => {
    onSubmit(getValues(values));
  };

  const initialValues = {
    modules: availableModules.reduce((acc, module) => {
      const license = data?.activeModuleLicenses.find(l => l.module === module);
      const limitSetting = getModuleLimitSetting(module, license?.level);
      acc[module] = {
        licenseId: license?.id,
        limit: license?.settings.prices?.[0][limitSetting],
      };
      return acc;
    }, {}),
    interval: getValidLicenseInterval({
      activeLicense: dataSource.activeLicense,
      startDate: dayjs(),
      interval: dataSource.activeLicense?.interval || LicenseInterval.Yearly,
    }),
    startDate: dayjs(),
    currency: SEK,
  };

  const onValuesChange = () => {
    onChange(getValues(form.getFieldsValue(true)));
  };

  useEffect(() => {
    form.resetFields();
    onValuesChange();
  }, [dataSource, data]);

  return (
    <Form form={form} onFinish={onFinish} onValuesChange={onValuesChange} layout="vertical" initialValues={initialValues}>
      {dataSource.type === DataSourceType.Website && (
        <Alert
          message={(
            <>
              See and compare all our plans.
              {' '}
              <a href={PLANS}>{PLANS}</a>
            </>
          )}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form.Item>
        <Row gutter={16}>
          {availableModules?.map(module => (
            <Col span={24 / availableModules.length} key={module}>
              <ModuleLicenseCard module={module} loading={loading} licenses={data?.activeModuleLicenses.filter(l => l.module === module)} form={form} addons={addons} onChange={onValuesChange} />
            </Col>
          ))}
        </Row>
      </Form.Item>

      <LicenseForm dataSource={dataSource} />
    </Form>
  );
};

export default ModuleLicenseForm;
